import type {ChatState} from '@microsoft/agent-host-protocol'
import type {TimelineTurn} from '@github-ui/ahp-relay/ahp-session-selectors'
import {getTurns, stringOrMarkdownToText} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {CheckpointAnchors, RelayCheckpoint} from '@github-ui/ahp-relay/session-checkpoints'
import {completedCheckpoints} from '@github-ui/ahp-relay/session-checkpoints'
import type {AhpSessionBundle} from '../../../utils/replay-ahp-events'

/** Anchor for a compaction tied to no turn. No turn has this id, so it renders at the tail. */
export const COMPACTION_MARKER_ANCHOR = '__ahp-compaction__'

const COMPACT_COMMAND_NAME = 'compact'
const TURN_STATE_ERROR = 'error'

/** A completed compaction, reduced to what the transcript row renders. */
export interface CompactionMarker {
  id: string
  afterTurnId: string | null
  checkpointNumber: number
  title?: string
  success: boolean
  tokensRemoved?: number
  messagesRemoved?: number
  error?: string
}

export function toCompactionMarker(checkpoint: RelayCheckpoint, afterTurnId: string | null): CompactionMarker {
  return {
    id: `compaction-${checkpoint.checkpointNumber}`,
    afterTurnId,
    checkpointNumber: checkpoint.checkpointNumber,
    ...(checkpoint.title === undefined ? {} : {title: checkpoint.title}),
    // A missing `success` counts as success, so a real compaction is never shown as failed.
    success: checkpoint.success !== false,
    ...(checkpoint.tokensRemoved === undefined ? {} : {tokensRemoved: checkpoint.tokensRemoved}),
    ...(checkpoint.messagesRemoved === undefined ? {} : {messagesRemoved: checkpoint.messagesRemoved}),
    ...(checkpoint.error === undefined ? {} : {error: checkpoint.error}),
  }
}

function pluralize(count: number, noun: string): string {
  return `${count.toLocaleString()} ${count === 1 ? noun : `${noun}s`}`
}

/** "114 tokens and 1 message", or undefined when the host reported neither. */
export function describeCompactionSavings(marker: CompactionMarker): string | undefined {
  const parts: string[] = []
  if (marker.tokensRemoved !== undefined) parts.push(pluralize(marker.tokensRemoved, 'token'))
  if (marker.messagesRemoved !== undefined) parts.push(pluralize(marker.messagesRemoved, 'message'))
  return parts.length > 0 ? parts.join(' and ') : undefined
}

/** The sentence a screen reader hears when a compaction lands. */
export function describeCompaction(marker: CompactionMarker): string {
  if (!marker.success) {
    return marker.error ? `Couldn't compact the conversation: ${marker.error}` : "Couldn't compact the conversation."
  }
  const savings = describeCompactionSavings(marker)
  return savings ? `Conversation compacted, removing ${savings}.` : 'Conversation compacted.'
}

/**
 * `/compact` commands, oldest first. Checkpoints carry no turn id, so the
 * command can supply an anchor. Errored commands are skipped: they never get a
 * checkpoint and would shift the pairing.
 */
function compactCommands(turns: readonly TimelineTurn[] | undefined): Array<{id: string; running: boolean}> {
  const commands: Array<{id: string; running: boolean}> = []
  for (const turn of turns ?? []) {
    if (turn.message?.origin?.kind !== 'user') continue
    if ('state' in turn && turn.state === TURN_STATE_ERROR) continue
    if (isCompactCommand(turn.message)) commands.push({id: turn.id, running: !('state' in turn)})
  }
  return commands
}

export function findCompactionCommandAnchor(
  turns: readonly TimelineTurn[],
  anchors: CheckpointAnchors,
): string | undefined {
  let latestAnchorIndex = -1
  for (const anchor of anchors.values()) {
    if (anchor === null) continue
    const index = turns.findIndex(turn => turn.id === anchor)
    if (index < 0) return undefined
    latestAnchorIndex = Math.max(latestAnchorIndex, index)
  }
  return compactCommands(turns).findLast(command => turns.findIndex(turn => turn.id === command.id) > latestAnchorIndex)
    ?.id
}

/** Whether the message is `/compact`, matching the first token case-insensitively as the host does. */
function isCompactCommand(message: TimelineTurn['message']): boolean {
  const rest = stringOrMarkdownToText(message.text)?.trim()
  if (!rest?.startsWith('/')) return false
  const name = rest.slice(1).split(/\s/, 1)[0] ?? ''
  return name.toLowerCase() === COMPACT_COMMAND_NAME
}

/**
 * Every turn the timeline renders, oldest first: saved turns plus the live
 * window. The live window alone can miss older `/compact` turns. A turn in
 * both keeps its saved position but takes the live version, which is newer.
 */
export function compactionAnchorTurns(
  state: ChatState | undefined,
  bundle: AhpSessionBundle | undefined,
): TimelineTurn[] {
  const savedState = (state && bundle?.chatStates[state.resource]) || bundle?.defaultChatState
  const saved = savedState ? (bundle?.timelineTurns?.[savedState.resource] ?? getTurns(savedState)) : []
  const live = state ? getTurns(state) : []
  const liveById = new Map(live.map(turn => [turn.id, turn]))
  const savedIds = new Set(saved.map(turn => turn.id))
  return [...saved.map(turn => liveById.get(turn.id) ?? turn), ...live.filter(turn => !savedIds.has(turn.id))]
}

/**
 * Compaction rows, oldest first, beneath their recorded turn or a matching `/compact`.
 *
 * Checkpoints without recorded anchors are paired with unclaimed `/compact` commands
 * from the newest, since the oldest are the ones that go missing. A checkpoint
 * can arrive before its `/compact` turn settles, so a running command counts
 * once there is a checkpoint for it. With more checkpoints than commands, some
 * were automatic and can't be told apart, so unanchored rows go to the tail rather
 * than under the wrong command.
 */
export function projectCompactionMarkers(
  checkpoints: readonly RelayCheckpoint[] | undefined,
  turns: readonly TimelineTurn[] | undefined,
  checkpointAnchors?: CheckpointAnchors,
): CompactionMarker[] {
  const completed = completedCheckpoints(checkpoints)
  if (completed.length === 0) return []

  const unanchored = completed.filter(checkpoint => !checkpointAnchors?.has(checkpoint.checkpointNumber))
  const claimed = new Set(
    completed.flatMap(checkpoint => {
      const anchor = checkpointAnchors?.get(checkpoint.checkpointNumber)
      return anchor === undefined ? [] : [anchor]
    }),
  )
  const commands = compactCommands(turns).filter(command => !claimed.has(command.id))
  const settled = commands.filter(command => !command.running)
  // A running /compact has no checkpoint until it lands, so it only takes part
  // when there is a checkpoint left over for it. Otherwise it would shift the rows.
  const anchors = unanchored.length > settled.length ? commands : settled
  const commandTurnIds = anchors.map(command => command.id)
  const offset = commandTurnIds.length - unanchored.length
  let index = 0
  return completed.map(checkpoint => {
    const recorded = checkpointAnchors?.get(checkpoint.checkpointNumber)
    if (recorded !== undefined) return toCompactionMarker(checkpoint, recorded)
    const anchor = offset < 0 ? COMPACTION_MARKER_ANCHOR : (commandTurnIds[offset + index] ?? COMPACTION_MARKER_ANCHOR)
    index++
    return toCompactionMarker(checkpoint, anchor)
  })
}
